import { Vector3 } from 'three';

import {
    GeometryModelGeometryType,
    PrefabModelType,
    type LayerModel,
    type PointModel,
    type PrefabModel
} from 'api/models';
import { calculateFullQuaternion } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    convexHull,
    signedArea,
    unionPolygons,
    type Polygon2D
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/geometry2d';
import {
    toView,
    type Vec2,
    type ViewFrame
} from 'app/modules/wallforming/components/scenes/objects/dimensions/core/viewFrame';

import type { Part } from 'app/modules/wallforming/components/scenes/objects/articles/types/part';

/**
 * Where the 2D silhouette of a part was derived from:
 * - `twoD`: dedicated TwoD* drawing layer whose plane is perpendicular to the view direction
 * - `faces`: union of all projected graphic faces (orthographic projection of the 3D geometry)
 * - `coverArea`: projected COVERAREA polygon (logic layer)
 */
export type ProfileSource = 'twoD' | 'faces' | 'coverArea';

export interface ViewProfile {
    source: ProfileSource;
    /** polygons in view coordinates of the frame */
    polygons: Polygon2D[];
}

interface LocalProfile {
    source: ProfileSource;
    /** polygons relative to the part position (already projected onto the view axes) */
    polygons: Polygon2D[];
}

const TWO_D_PREFIX = 'TwoD';
const MIN_AREA = 1e-6;
const MIN_FACE_AREA = 1e-8;
const PLANAR_TOLERANCE = 1e-3;
const MAX_FACES_FOR_UNION = 800;
const MAX_CACHE_ENTRIES = 500;

const LOCAL_AXES = [new Vector3(1, 0, 0), new Vector3(0, 1, 0), new Vector3(0, 0, 1)];
/** in-plane axes (first, second) of the standard 2D view along each local axis */
const VIEW_PLANE_AXES: [number, number][] = [
    [1, 2],
    [0, 2],
    [0, 1]
];

const profileCache = new Map<string, LocalProfile | null>();

function collectLayers(prefab: PrefabModel, kind: 'graphicLayers' | 'logicLayers'): LayerModel[] {
    // nested non-SubPart children share the coordinate system of their parent prefab
    const layers = [...(prefab[kind] ?? [])];
    for (const child of prefab.children ?? []) {
        if (child.type !== PrefabModelType.SubPart) {
            layers.push(...collectLayers(child, kind));
        }
    }
    return layers;
}

const isTwoDLayer = (layer: LayerModel) => layer.layerType.startsWith(TWO_D_PREFIX);

function project(p: PointModel, a: Vector3, b: Vector3): Vec2 {
    return { x: p.x * a.x + p.y * a.y + p.z * a.z, y: p.x * b.x + p.y * b.y + p.z * b.z };
}

function totalArea(polygons: Polygon2D[]): number {
    return polygons.reduce((sum, polygon) => sum + Math.abs(signedArea(polygon[0] ?? [])), 0);
}

function isClosed(points: PointModel[]): boolean {
    if (points.length < 4) {
        return false;
    }
    const first = points[0];
    const last = points[points.length - 1];
    return Math.abs(first.x - last.x) + Math.abs(first.y - last.y) + Math.abs(first.z - last.z) < PLANAR_TOLERANCE;
}

/** Builds the silhouette out of closed geometries; open line work only contributes to a hull fallback. */
function silhouetteOfLayers(layers: LayerModel[], a: Vector3, b: Vector3): Polygon2D[] {
    const rings: Vec2[][] = [];
    const loosePoints: Vec2[] = [];

    for (const layer of layers) {
        for (const geometry of layer.geometries) {
            const projected = geometry.points.map((p) => project(p, a, b));
            const isArea =
                geometry.geometryType === GeometryModelGeometryType.Face ||
                geometry.geometryType === GeometryModelGeometryType.Hatch ||
                (geometry.geometryType === GeometryModelGeometryType.Polyline && isClosed(geometry.points));

            if (isArea && projected.length >= 3 && Math.abs(signedArea(projected)) > MIN_FACE_AREA) {
                rings.push(projected);
            } else {
                loosePoints.push(...projected);
            }
        }
    }

    if (rings.length > MAX_FACES_FOR_UNION) {
        return [[convexHull(rings.flat())]];
    }
    if (rings.length > 0) {
        return unionPolygons(rings);
    }
    if (loosePoints.length >= 3) {
        const hull = convexHull(loosePoints);
        return hull.length >= 3 ? [[hull]] : [];
    }
    return [];
}

/** Returns the axis (0 = x, 1 = y, 2 = z) in which the layer is planar, or null. */
function getPlanarAxis(layer: LayerModel): number | null {
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (const geometry of layer.geometries) {
        for (const p of geometry.points) {
            const c = [p.x, p.y, p.z];
            for (let i = 0; i < 3; i++) {
                min[i] = Math.min(min[i], c[i]);
                max[i] = Math.max(max[i], c[i]);
            }
        }
    }

    const extents = max.map((v, i) => v - min[i]);
    if (!extents.every(Number.isFinite)) {
        return null;
    }
    const largest = Math.max(...extents);
    const axis = extents.findIndex((e) => e <= PLANAR_TOLERANCE * Math.max(largest, 1));
    return largest > PLANAR_TOLERANCE && axis >= 0 ? axis : null;
}

/** Picks the TwoD layer that is drawn in a plane perpendicular to the given local axis. */
function selectTwoDLayer(prefab: PrefabModel, viewAxis: number): LayerModel | null {
    const candidates = [...collectLayers(prefab, 'graphicLayers'), ...collectLayers(prefab, 'logicLayers')].filter(
        isTwoDLayer
    );
    return candidates.find((layer) => getPlanarAxis(layer) === viewAxis) ?? null;
}

/** Standard (unrotated) 2D view of the prefab along one of its local axes, in the coordinates of VIEW_PLANE_AXES. */
function computeLocalProfile(prefab: PrefabModel, viewAxis: number): LocalProfile | null {
    const [first, second] = VIEW_PLANE_AXES[viewAxis];
    const a = LOCAL_AXES[first];
    const b = LOCAL_AXES[second];

    const twoDLayer = selectTwoDLayer(prefab, viewAxis);
    if (twoDLayer) {
        const polygons = silhouetteOfLayers([twoDLayer], a, b);
        if (totalArea(polygons) > MIN_AREA) {
            return { source: 'twoD', polygons };
        }
    }

    const graphicLayers = collectLayers(prefab, 'graphicLayers').filter((layer) => !isTwoDLayer(layer));
    if (graphicLayers.length > 0) {
        const polygons = silhouetteOfLayers(graphicLayers, a, b);
        if (totalArea(polygons) > MIN_AREA) {
            return { source: 'faces', polygons };
        }
    }

    const coverAreaLayers = collectLayers(prefab, 'logicLayers').filter((layer) => layer.layerType === 'COVERAREA');
    if (coverAreaLayers.length > 0) {
        const points = coverAreaLayers.flatMap((layer) =>
            layer.geometries.flatMap((geometry) => geometry.points.map((p) => project(p, a, b)))
        );
        const hull = convexHull(points);
        if (hull.length >= 3 && Math.abs(signedArea(hull)) > MIN_AREA) {
            return { source: 'coverArea', polygons: [[hull]] };
        }
    }

    return null;
}

function dominantAxis(v: Vector3): number {
    const abs = [Math.abs(v.x), Math.abs(v.y), Math.abs(v.z)];
    return abs.indexOf(Math.max(...abs));
}

/**
 * Projects a placed prefab part onto the view plane of the frame.
 *
 * The part is always drawn from its standard 2D view (TwoD layer or projected faces along the local axis that
 * points closest to the viewer). That profile is computed in axis-aligned local coordinates (stable, cached once per
 * prefab and axis) and then rotated into the view: for a local point L the view coordinate is
 * x = L·(s⊙q⁻¹u) + (p − o)·u, restricted to the two in-plane local axes. The depth component is dropped,
 * so rotated parts look exactly like their unrotated drawing, just turned.
 */
export function getPartViewProfile(prefab: PrefabModel, part: Part, frame: ViewFrame): ViewProfile | null {
    const inverse = calculateFullQuaternion(part.alignment, part.rotation).invert();
    const scale = part.scale ?? new Vector3(1, 1, 1);
    const a = frame.u.clone().applyQuaternion(inverse).multiply(scale);
    const b = frame.v.clone().applyQuaternion(inverse).multiply(scale);
    const viewAxis = dominantAxis(frame.n.clone().applyQuaternion(inverse));

    const key = `${prefab.id}|${prefab.contentHash ?? ''}|${viewAxis}`;
    let local = profileCache.get(key);
    if (local === undefined) {
        local = computeLocalProfile(prefab, viewAxis);
        if (profileCache.size >= MAX_CACHE_ENTRIES) {
            profileCache.delete(profileCache.keys().next().value as string);
        }
        profileCache.set(key, local);
    }
    if (!local) {
        return null;
    }

    const [first, second] = VIEW_PLANE_AXES[viewAxis];
    const ax = a.getComponent(first);
    const ay = a.getComponent(second);
    const bx = b.getComponent(first);
    const by = b.getComponent(second);
    const offset = toView(frame, part.position);
    const transform = (p: Vec2): Vec2 => ({
        x: p.x * ax + p.y * ay + offset.x,
        y: p.x * bx + p.y * by + offset.y
    });
    // a mirroring transform flips the ring orientation; keep outer rings counter-clockwise
    const mirrored = ax * by - ay * bx < 0;

    return {
        source: local.source,
        polygons: local.polygons.map((polygon) =>
            polygon.map((ring) => {
                const mapped = ring.map(transform);
                return mirrored ? mapped.reverse() : mapped;
            })
        )
    };
}

/** Yields every prefab (representation or SubPart child) that has a placed part in the instance. */
export function getPlacedPrefabs(
    representation: PrefabModel,
    parts: Record<string, Part>
): { prefab: PrefabModel; part: Part }[] {
    const prefabs = [
        representation,
        ...(representation.children ?? []).filter((child) => child.type === PrefabModelType.SubPart)
    ];
    return prefabs.flatMap((prefab) => (parts[prefab.name] ? [{ prefab, part: parts[prefab.name] }] : []));
}
